/**
 * Sentry Client-Side Configuration
 *
 * IMPORTANT: @sentry/remix must be installed before this will work.
 * Run: npm install @sentry/remix
 *
 * The SENTRY_DSN is injected via window.ENV in root.tsx loader.
 *
 * Audit 2026-05-11 (post-cutover Sentry triage):
 *   - Adds a `beforeSend` noise filter for known-benign exception types
 *     that fired heavily after the diolo.com cutover but aren't
 *     application bugs (aborted fetches, mobile network glitches,
 *     post-deploy stale-chunk loads). Each is documented inline.
 *   - Adds a React-error-code decoder: production React errors arrive
 *     with the opaque message "Minified React error #NNN; visit
 *     https://react.dev/errors/NNN?…" — we extract the numeric code
 *     and re-issue the event to Sentry with the code as a tag so the
 *     Issues view groups by actual error type (#185 infinite loop,
 *     #310 hook order, #418/423 hydration, etc.) instead of one giant
 *     opaque bucket. This was the missing-link diagnosis for the
 *     Clarity-reported "Minified React error" pattern in admin views.
 */
import * as Sentry from "@sentry/remix";
import { useLocation, useMatches } from "@remix-run/react";
import { useEffect } from "react";

/**
 * Patterns the client should never ship to Sentry. Match by exception
 * type + value substring. Keep the list short and explicit — broad
 * regex matches risk swallowing real bugs.
 */
const NOISE_PATTERNS: Array<{ type?: string; value: string }> = [
  // User navigated away mid-request. Browser cancelled the fetch.
  // Not a bug — happens on every back-button click during a load.
  { type: "AbortError", value: "" },
  { value: "query() call aborted" },
  // Remix v2 surfaces the same abort as `queryRoute() call aborted`
  // (loader-grain abort). Added 2026-05-13 after seeing 24h Sentry
  // feed list this variant separately from `query() call aborted`.
  { value: "queryRoute() call aborted" },

  // Generic Safari/WebKit network failure — appears as
  // `TypeError: Load failed`. Common on flaky mobile networks; the
  // failed request retries on next nav. No app code fault.
  { type: "TypeError", value: "Load failed" },

  // iOS in-app browsers (Instagram/Facebook/Zalo/Threads) inject a
  // `window.webkit.messageHandlers` bridge for native ↔ webview
  // messaging. Third-party scripts (Meta Pixel, OnePay, payment SDKs,
  // misc analytics shims) sometimes probe it without a typeof guard.
  // Outside iOS in-app browsers the property is undefined and the
  // access throws. We don't ship this code — fixing it requires the
  // vendor to patch. Filter the noise so it doesn't crowd out real
  // bugs in the Sentry feed.
  { value: "window.webkit.messageHandlers" },
  { value: "webkit.messageHandlers" },

  // LastPass / 1Password / Bitwarden / other password-manager browser
  // extensions inject an `_autofillcallbackhandler` global to drive
  // their autofill UX. The injection sometimes races our hydration on
  // /account/login + /checkout — the extension throws "can't find
  // variable: _autofillcallbackhandler" before its bootstrap finishes.
  // Not our code, not our bug. Clarity surfaced this 7-day at 0.58% of
  // JS errors; Sentry would too. Filter to clean the feed.
  { value: "_autofillcallbackhandler" },
  // Generic Chrome-extension content-script crash trying to read a
  // page's iframes. Surfaces as
  // `null is not an object (evaluating 'a.contentwindow.document')`.
  // Filter the substring that uniquely identifies it.
  { value: "a.contentwindow.document" },

  // Post-deploy stale chunk: the client has cached an old bundle
  // manifest pointing to a hashed JS file that the new server no
  // longer serves. Browser throws "Importing a module script failed".
  // A hard refresh resolves it. Tracking these in Sentry is signal
  // that we deployed, not signal of a bug.
  { value: "Importing a module script failed" },
  { value: "Failed to fetch dynamically imported module" },

  // Audit 2026-05-21: Sentry triage found 4 cohorts of recurring noise
  // that bury real bugs in the Issues feed. Suppressing them:

  // Microsoft Clarity (scripts.clarity.ms/clarity.js) — Microsoft's
  // session-recording SDK mutates the DOM during React hydration,
  // racing React's reconciliation. React fires #418/#423 hydration
  // errors and Clarity's own MutationObserver then accesses a DOM node
  // that no longer matches the tree it expected → TypeError on
  // `Vt[r].children` etc. inside clarity.js. 360 events in 30d, all
  // inside the clarity bundle, none in our code. We can't fix Clarity's
  // race against React 18 from our side; either disable Clarity or
  // accept the noise. We're keeping Clarity for the session replays
  // but filtering out the spam.
  { value: "Vt[r].children" },
  { value: "scripts.clarity.ms" },

  // Zalo's JavaScript SDK (zaloJSV2 global). Loaded inside the Zalo
  // in-app browser to support the Zalo Mini App API. When customers
  // open our site from a regular browser, zaloJSV2 is never injected
  // and any code probing for it throws ReferenceError. Third-party,
  // not our code; the probe is from a Vietnamese analytics shim. 33
  // events in 30d across 4 separate URL fingerprints. Filter.
  { value: "Can't find variable: zaloJSV2" },
  { value: "zaloJSV2 is not defined" },

  // Android WebView "Java object is gone" — the in-app browser
  // destroyed its JS↔Java bridge before our JS finished using it.
  // Happens when the user backgrounds/foregrounds the host app
  // (Instagram, Facebook, Threads, Zalo). The bridge is invalidated
  // and any postMessage / clickedMetaDataLogging / etc. call throws.
  // We can't repair the bridge from page-side; filter.
  { value: "Java object is gone" },

  // Cross-origin / sandboxed iframe access denied. Fires when a
  // third-party tracking pixel (Meta Pixel, GA, TikTok Pixel, etc.)
  // probes contentWindow.document on an iframe whose origin no
  // longer matches. Browser security guard, not actionable from our
  // side.
  { type: "SecurityError", value: "The operation is insecure" },

  // Email bounce notifications from the Resend webhook handler. These
  // are INFO-level events we deliberately log to surface deliverability
  // problems — they're not errors. Should never have been in Sentry's
  // Issue feed; suppress so it stops bumping un-actionable rows to
  // High priority.
  { value: "Email email.bounced" },
];

function isNoise(event: Sentry.ErrorEvent): boolean {
  const ex = event.exception?.values?.[0];
  if (!ex) return false;
  const type = ex.type || "";
  const value = ex.value || "";
  return NOISE_PATTERNS.some(
    (p) =>
      (!p.type || p.type === type) &&
      (!p.value || value.includes(p.value)),
  );
}

/**
 * React's production error decoder format:
 *   "Minified React error #185; visit https://react.dev/errors/185?args[]=… for the full message"
 * (older React 17 form pointed at reactjs.org/docs/error-decoder.html?invariant=185).
 *
 * Both forms carry the numeric code, which is exactly what we want for
 * grouping. Return the code (as a string) or null if the message isn't
 * a minified React error.
 */
function extractReactErrorCode(message: string): string | null {
  if (!message) return null;
  // Form 1: "Minified React error #185"
  const m1 = message.match(/Minified React error #(\d+)/);
  if (m1) return m1[1];
  // Form 2: ?invariant=NNN URL fragment (older React)
  const m2 = message.match(/[?&]invariant=(\d+)/);
  if (m2) return m2[1];
  return null;
}

function decorateReactErrors(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  const ex = event.exception?.values?.[0];
  if (!ex) return event;
  const message = ex.value || "";
  const code = extractReactErrorCode(message);
  if (!code) return event;
  // Tag with the code so the Sentry Issues view groups React errors
  // by their actual kind. Also extend the type so the title is
  // unambiguous at a glance.
  event.tags = { ...(event.tags || {}), reactErrorCode: code };
  ex.type = `MinifiedReactError#${code}`;
  return event;
}

/**
 * Audit 2026-05-17: forward React minified errors from console.error to
 * Sentry as captured exceptions.
 *
 * Why this exists: React's hydration errors (#418 text-content-mismatch,
 * #423 error-while-hydrating, #425 timestamp mismatch, etc.) don't
 * `throw` — React catches them internally and logs via console.error
 * while client-side-rendering the affected subtree. Sentry's default
 * integrations (uncaughtError, unhandledRejection) DO NOT capture these.
 * Result: Clarity recordings showed dozens of "Minified React error
 * #418/#423" instances in user sessions, but Sentry's Issues view was
 * empty for them — no stack traces to triage.
 *
 * This interceptor catches ONLY messages that match the React minified-
 * error format (avoids logging every console.error in the app). The
 * captured exception flows through Sentry's normal pipeline including
 * the `decorateReactErrors` beforeSend hook, so the resulting issue
 * groups by error code (#418 vs #423 vs #310 etc.) and includes the
 * call stack from wherever React was hydrating when it noticed the
 * mismatch — exactly what we need to fix the actual bug.
 *
 * Stays no-op outside production (matches beforeSend's localhost skip).
 */
function installReactConsoleErrorForwarder() {
  if (typeof window === "undefined") return;
  if (window.location.hostname === "localhost") return;
  const original = console.error.bind(console);
  console.error = function (...args: unknown[]) {
    try {
      // React's minified error format always has the literal substring
      // "Minified React error #" as one of the first arguments. Cheap to
      // detect via stringified first arg.
      const first = args.length > 0 ? String(args[0]) : "";
      if (first.includes("Minified React error #")) {
        // Capture with a real Error so Sentry collects a stack trace
        // pointing at the React internals + caller. The decorateReactErrors
        // beforeSend hook then tags it with reactErrorCode + retypes the
        // event to MinifiedReactError#NNN for clean grouping.
        Sentry.captureException(new Error(first), {
          tags: { source: "console-error-forwarder" },
          extra: { allArgs: args.map((a) => String(a)) },
        });
      }
    } catch {
      // Never let our interceptor break the original console.error path
    }
    // Always call through so dev tools still show the message
    return original.apply(console, args as Parameters<typeof console.error>);
  };
}

export function initSentryClient(dsn: string) {
  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0, // Disable Session Replay by default
    replaysOnErrorSampleRate: 0, // Enable if @sentry/replay is added later
    integrations: [
      Sentry.browserTracingIntegration({
        useEffect,
        useLocation,
        useMatches,
      }),
    ],
    beforeSend(event) {
      // Don't send events in development
      if (window.location.hostname === "localhost") return null;
      // Drop known-benign noise (aborted fetches, stale chunks, etc.)
      if (isNoise(event)) return null;
      // Tag wholesale-portal events so we can filter/alert separately
      // from the B2C storefront in the Sentry Issues view. Both the
      // legacy /portal path and the new /partners alias get the tag.
      // Audit 2026-05-17: Sprint 1 of /partners stand-up.
      if (typeof window !== "undefined") {
        const path = window.location.pathname;
        if (path.startsWith("/portal") || path.startsWith("/partners")) {
          event.tags = { ...(event.tags || {}), source: "portal" };
        }
      }
      // Decode React's opaque minified error codes for better grouping
      return decorateReactErrors(event);
    },
  });
  // Wire the console.error → Sentry forwarder AFTER init so we don't
  // accidentally call into Sentry before it's ready.
  installReactConsoleErrorForwarder();
}

export { Sentry };
